import { FC, useMemo } from "react";
import { Box, LinearProgress, Stack } from "@mui/material";
import { useWillUnmount } from "rooks";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { resourceDetailsTargetResourceIdSelector } from "@/store/resource-details/selectors/target-resource/resourceDetailsTargetResourceIdSelector";
import { savingsOpportunitiesDataSelector } from "@/store/savings-opportunities/selectors/savingsOpportunitiesDataSelector";
import { setSavingsOpportunitiesSelectedRowsIds } from "@/store/savings-opportunities/savingsOpportunitiesSlice";
import { savingsOpportunitiesLoadingSelector } from "@/store/savings-opportunities/selectors/savingsOpportunitiesLoadingSelector";
import { ResourceDetailsOpportunitiesContentToolbar } from "@/components/pages/common/resource-details/components/content/opportunities-content/components/ResourceDetailsOpportunitiesContentToolbar";
import { ResourceDetailsOpportunityRow } from "@/components/pages/common/resource-details/components/content/opportunities-content/components/ResourceDetailsOpportunityRow";

export const ResourceDetailsOpportunitiesTabContent: FC = () => {
  const dispatch = useAppDispatch();

  const resourceId = useAppSelector(resourceDetailsTargetResourceIdSelector);
  const loading = useAppSelector((state) =>
    savingsOpportunitiesLoadingSelector(state, resourceId),
  );
  const opportunities = useAppSelector((state) =>
    savingsOpportunitiesDataSelector(state, resourceId),
  );

  const highest = useMemo(() => {
    if ((opportunities?.length ?? 0) < 2) {
      return;
    }

    return opportunities?.reduce((highest, opportunity) => {
      const currentCost = opportunity.monthly_savings ?? 0;
      const highestCost = highest?.monthly_savings ?? 0;

      if (currentCost > highestCost) {
        return opportunity;
      }

      return highest;
    });
  }, [opportunities]);

  useWillUnmount(() => {
    dispatch(setSavingsOpportunitiesSelectedRowsIds([]));
  });

  return (
    <Stack height="100%" p={1.5} spacing={1}>
      {opportunities && (
        <ResourceDetailsOpportunitiesContentToolbar
          opportunities={opportunities}
        />
      )}

      <Stack height="100%" spacing={2.5}>
        {loading && (
          <Box position="absolute" top={0} left={0} right={0}>
            <LinearProgress />
          </Box>
        )}

        {highest && (
          <ResourceDetailsOpportunityRow highest opportunity={highest} />
        )}

        {opportunities?.map((opportunity) => {
          if (opportunity.id === highest?.id) {
            return null;
          }

          return (
            <ResourceDetailsOpportunityRow
              key={opportunity.id}
              opportunity={opportunity}
              singleOne={opportunities?.length === 1}
            />
          );
        })}
      </Stack>
    </Stack>
  );
};
